import { useCallback, useEffect, useRef, useState } from 'react';
import { buildStudioCompositeImageUrl, toStockNumber } from '|/helpers/vehicle-image';
import useEventHelper from '|/hooks/use-event-helper';

export interface VehicleStudioImage {
  url: string;
  onError: () => void;
}

interface StudioImageOptions {
  width: number;
  isEnabled?: boolean;
}

const useVehicleStudioImage = (
  stockNumberInput: number | string | null | undefined,
  { width, isEnabled = true }: StudioImageOptions
): VehicleStudioImage | null => {
  const { sendEvent } = useEventHelper();
  const stockNumber = toStockNumber(stockNumberInput);
  const [erroredStockNumber, setErroredStockNumber] = useState<string | null>(null);
  const reported = useRef(new Set<string>());

  const hasErrored = !!stockNumber && erroredStockNumber === stockNumber;

  const onError = useCallback(() => {
    setErroredStockNumber(stockNumber);
  }, [stockNumber]);

  useEffect(() => {
    if (!isEnabled || !hasErrored || !stockNumber) return;
    if (reported.current.has(stockNumber)) return;

    reported.current.add(stockNumber);
    sendEvent('pp_vehicle_image_fallback', { rung: 'studio-composite-errored', stockNumber });
  }, [isEnabled, hasErrored, stockNumber, sendEvent]);

  if (!isEnabled || hasErrored) return null;

  const url = buildStudioCompositeImageUrl({ stockNumber, width });

  return url ? { url, onError } : null;
};

export default useVehicleStudioImage;
